import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { templatesActions } from "../../redux/templates/templatesSlice";
import type { TemplateCategory } from "../../redux/templates/templatesTypes";
import { getToken } from "../../config/getToken";

const createScopeKey = (
  platformId?: string,
  clientId?: string,
  templateCategory?: TemplateCategory,
): string => `${templateCategory ?? ""}|${platformId ?? ""}|${clientId ?? ""}`;

const determineTemplateCategory = (
  platformId?: string,
  clientId?: string,
): TemplateCategory => {
  if (platformId && clientId) return "CLIENT";
  if (platformId) return "PLATFORM";
  return "OPTUM";
};

interface UseGetTemplatesOptions {
  platformId?: string;
  clientId?: string;
  enabled?: boolean;
}

export const useGetTemplates = ({
  platformId,
  clientId,
  enabled = true,
}: UseGetTemplatesOptions) => {
  const dispatch = useAppDispatch();
  const lastDispatchedKeyRef = useRef<string | null>(null);
  const optumFetchedRef = useRef(false);
  const platformFetchedForPlatformIdRef = useRef<string | null>(null);
  const dataByCategory = useAppSelector(
    (state) => state.templates.dataByCategory,
  );
  const loading = useAppSelector((state) => state.templates.loading);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
      const token = await getToken();
      if (cancelled || !token) return;

    if (!optumFetchedRef.current) {
      dispatch(
        templatesActions.getTemplatesRequested({
          platformId: "",
          clientId: "",
          templateCategory: "OPTUM",
          token,
        }),
      );
      optumFetchedRef.current = true;
    }
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [enabled, dispatch]);

  useEffect(() => {
    if (!enabled || loading) {
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
    const token = await getToken();
    if (cancelled || !token) return;

    const hasOptumData = !!dataByCategory.OPTUM;

    if (
      platformId &&
      platformFetchedForPlatformIdRef.current !== platformId &&
      hasOptumData
    ) {
      dispatch(
        templatesActions.getTemplatesRequested({
          platformId,
          clientId: "",
          templateCategory: "PLATFORM",
          token,
        }),
      );
      platformFetchedForPlatformIdRef.current = platformId;
    }
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [platformId, enabled, dataByCategory.OPTUM, loading, dispatch]);

  useEffect(() => {
    if (!enabled || loading) {
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
    const token = await getToken();
    if (cancelled || !token) return;

    const hasOptumData = !!dataByCategory.OPTUM;
    const hasPlatformData = !!dataByCategory.PLATFORM;

    if (!platformId || !clientId) {
      return;
    }

    const category = determineTemplateCategory(platformId, clientId);
    if (category !== "CLIENT") {
      return;
    }

    if (!hasOptumData || (platformId && !hasPlatformData)) {
      return;
    }

    const scopeKey = createScopeKey(platformId, clientId, category);
    if (scopeKey === lastDispatchedKeyRef.current) {
      return;
    }

    lastDispatchedKeyRef.current = scopeKey;
    dispatch(
      templatesActions.getTemplatesRequested({
        platformId,
        clientId,
        templateCategory: category,
        token,
      }),
    );
    };

    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [
    platformId,
    clientId,
    enabled,
    dataByCategory.OPTUM,
    dataByCategory.PLATFORM,
    loading,
    dispatch,
  ]);
};
